<template>
    <popover ref="communityAttentionPopover" class="community-attention-popover-wrapper" @hide="markCommunityAttentionDismissed">
        <div class="community-attention-popover">
            <h3>{{ communityAttentionTitle }}</h3>
            <p>Since you last checked your feed.</p>
            <div class="community-attention-list">
                <button
                    v-for="item in communityAttentionPreviewItems"
                    :key="item.id"
                    type="button"
                    class="community-attention-list-item"
                    @click="openAttentionItem(item)"
                >
                    {{ item.label }}
                </button>
                <span v-if="communityAttentionItems.length > 3" class="more-indicator">
                    +{{ communityAttentionItems.length - 3 }}
                </span>
            </div>
            <div class="community-attention-actions">
                <primevue-button class="plain-btn" @click="openNotifications">
                    Open notifications
                    <font-awesome-icon :icon="['fas', 'arrow-right']" />
                </primevue-button>
            </div>
        </div>
    </popover>
</template>

<script setup lang="ts">
import {
    computed, nextTick, ref, watch,
} from 'vue';
import { authState } from '@lcp/http-auth';
import Popover from 'primevue/popover';
import PrimevueButton from 'primevue/button';
import { storeToRefs } from 'pinia';
import { useUIStore } from '@/stores/uiStore.ts';
import { useDeltaUserSettingsStore } from '@/stores/deltaGpt/deltaGptUserSettingsStore.ts';
import { IDocumentSimple } from '@/interfaces/deltaGpt/IDocument.ts';
import { useEnactNotifications } from '@/composables/powerMarkets/useEnactNotifications.ts';
import { useCommunityFeed } from '@/composables/community/useCommunityFeed.ts';

const MAX_PREVIEW_ITEMS = 3;

const props = defineProps<{
    documents: Array<IDocumentSimple>;
}>();

const emit = defineEmits<{
    'open-document': [IDocumentSimple];
    'open-enact-notifications': [];
    'open-notifications': [];
    'new-feed-count-updated': [number];
}>();

type TCommunityAttentionItem = {
    id: string;
    label: string;
    type: 'document' | 'enactNotifications';
    document?: IDocumentSimple;
};

const uiStore = useUIStore();
const deltaUserSettingsStore = useDeltaUserSettingsStore();
const { isMobileView } = storeToRefs(uiStore);
const { unseenNotifications } = useEnactNotifications();
const {
    getDocumentFeedTimestamp,
    getFeedAttentionDismissedTimestamp,
    isNewAttentionDocument,
} = useCommunityFeed();
const communityAttentionPopover = ref<InstanceType<typeof Popover> | null>(null);
const enactNotificationAttentionTimestamp = ref<number>(0);
const lastUnseenNotificationCount = ref<number>(0);
const lastAutoOpenedCommunityAttentionTimestamp = ref<number>(0);

const canViewCommunity = computed<boolean>(() => authState.value.loggedIn);
const newUserFeedDocumentCount = computed<number>(() => props.documents.filter(isNewAttentionDocument).length);
const communityAttentionDocuments = computed<Array<IDocumentSimple>>(() => props.documents
    .filter(isNewAttentionDocument));
const enactNotificationsAttentionItem = computed<TCommunityAttentionItem | null>(() => {
    if (unseenNotifications.value === 0) return null;
    if (enactNotificationAttentionTimestamp.value <= getFeedAttentionDismissedTimestamp()) return null;
    return {
        id: 'enact-notifications',
        label: unseenNotifications.value === 1 ? '1 new Enact notification' : `${formatCountBadgeLabel(unseenNotifications.value)} new Enact notifications`,
        type: 'enactNotifications',
    };
});
const communityAttentionItems = computed<Array<TCommunityAttentionItem>>(() => [
    ...communityAttentionDocuments.value.map(document => ({
        id: document.documentShortCode,
        label: document.documentTitle,
        type: 'document' as const,
        document,
    })),
    ...(enactNotificationsAttentionItem.value ? [enactNotificationsAttentionItem.value] : []),
]);
const communityAttentionPreviewItems = computed<Array<TCommunityAttentionItem>>(() => communityAttentionItems.value.slice(0, MAX_PREVIEW_ITEMS));
const communityAttentionTotalCount = computed<number>(() => (
    communityAttentionDocuments.value.length
    + (enactNotificationsAttentionItem.value ? unseenNotifications.value : 0)
));
const communityAttentionTitle = computed<string>(() => (
    communityAttentionTotalCount.value === 1
        ? '1 new item'
        : `${formatCountBadgeLabel(communityAttentionTotalCount.value)} new items`
));
const latestCommunityAttentionTimestamp = computed<number>(() => Math.max(
    0,
    ...communityAttentionDocuments.value.map(getDocumentFeedTimestamp),
    enactNotificationsAttentionItem.value ? enactNotificationAttentionTimestamp.value : 0,
));

function show (anchor: HTMLElement): void {
    communityAttentionPopover.value?.show({
        currentTarget: anchor,
        target: anchor,
    } as unknown as Event);
}

function hide (): void {
    communityAttentionPopover.value?.hide();
}

function openAttentionItem (item: TCommunityAttentionItem): void {
    hide();
    if (item.type === 'document' && item.document) {
        emit('open-document', item.document);
        return;
    }
    emit('open-enact-notifications');
}

function openNotifications (): void {
    hide();
    emit('open-notifications');
}

async function markCommunityAttentionDismissed (): Promise<void> {
    await deltaUserSettingsStore.dismissFeedAttention();
}

function formatCountBadgeLabel (count: number): string {
    return count > 99 ? '99+' : `${count}`;
}

async function maybeShowCommunityAttentionPopover (): Promise<void> {
    if (!canViewCommunity.value || isMobileView.value) return;
    if (latestCommunityAttentionTimestamp.value === 0) return;
    if (latestCommunityAttentionTimestamp.value <= lastAutoOpenedCommunityAttentionTimestamp.value) return;

    await nextTick();
    const communityButtonElement = document.querySelector<HTMLElement>('.feature-button');
    if (!communityButtonElement) return;

    lastAutoOpenedCommunityAttentionTimestamp.value = latestCommunityAttentionTimestamp.value;
    show(communityButtonElement);
}

watch(unseenNotifications, (count) => {
    if (count === 0) {
        enactNotificationAttentionTimestamp.value = 0;
    } else if (count > lastUnseenNotificationCount.value) {
        enactNotificationAttentionTimestamp.value = Date.now();
    }

    lastUnseenNotificationCount.value = count;
}, { immediate: true });

watch(newUserFeedDocumentCount, count => {
    emit('new-feed-count-updated', count);
}, { immediate: true });

watch(latestCommunityAttentionTimestamp, () => {
    maybeShowCommunityAttentionPopover();
}, { immediate: true });

defineExpose({ show, hide });
</script>

<style scoped lang="scss">
.community-attention-popover {
    display: flex;
    flex-direction: column;
    gap: var(--defaultSpacing);
    max-width: 34rem;
    padding: var(--defaultSpacing);

    h3,
    p {
        margin: 0;
    }

    h3 {
        color: var(--accentColour);
        font-size: var(--defaultFontSize);
    }

    p {
        color: var(--neutral700);
        font-size: var(--smallFontSize);
    }
}

.community-attention-list {
    display: flex;
    flex-direction: column;
    gap: var(--extraSmallSpacing);

    .more-indicator {
        color: var(--neutral700);
        font-size: var(--smallFontSize);
        margin: var(--smallSpacing);
        font-style: italic;
    }
}

.community-attention-list-item {
    background: var(--neutral100);
    border: 1px solid var(--neutral300);
    border-radius: var(--borderRadiusOuter);
    color: var(--primaryFont);
    cursor: pointer;
    padding: var(--smallSpacing) var(--defaultSpacing);
    text-align: left;

    &:hover {
        border-color: var(--accentColour);
    }
}

.community-attention-actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--smallSpacing);
}

.community-attention-popover-wrapper :deep(.p-popover-content) {
    padding: var(--defaultSpacing);
}
</style>
